import { useCallback, useMemo } from "react";
import {
  useQueryParams,
  StringParam,
  NumberParam,
  BooleanParam,
  DelimitedArrayParam,
} from "use-query-params";
import mapValues from "lodash/mapValues";
import isArray from "lodash/isArray";
import omitBy from "lodash/omitBy";
import isUndefined from "lodash/isUndefined";

import { KontaxTransactionFilterOptions } from "../../types";

type ParamName = keyof typeof PARAM_CONFIG;

const PARAM_CONFIG = {
  email: StringParam,
  direction: NumberParam,
  source: StringParam,
  dateFrom: StringParam,
  dateTo: StringParam,
  vatRates: DelimitedArrayParam,
  categories: DelimitedArrayParam,
  categorized: BooleanParam,
  vatCategoryCodes: DelimitedArrayParam,
  accountIds: DelimitedArrayParam,
};

const useTransactionFilterParams = () => {
  const [queryParams, setQueryParams] = useQueryParams(PARAM_CONFIG);

  const params = useMemo(() => omitBy(queryParams, isUndefined), [queryParams]);

  const setParams = useCallback(
    (changes: Record<string, any>) => {
      const newParams = mapValues(changes, (value, key) => {
        /*
         * For a boolean param an explicit `false` is a filter value, not an
         * empty one: `categorized: false` means "not booked". Keep it, or the
         * blanket falsy check below would strip it back out of the URL.
         * Non-boolean params keep the old behaviour.
         */
        if (
          value === false &&
          PARAM_CONFIG[key as ParamName] === BooleanParam
        ) {
          return value;
        }
        if (!value || (isArray(value) && value.length === 0)) {
          return undefined; // replace empty values with `undefined` to remove param from url
        }
        return value;
      });
      setQueryParams(newParams, "replaceIn"); // replace only passed parameters, leaving the rest as is
    },
    [setQueryParams]
  );

  return [params, setParams] as [
    Partial<KontaxTransactionFilterOptions>,
    (changes: Partial<KontaxTransactionFilterOptions>) => void
  ];
};

export default useTransactionFilterParams;
